import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { SEP_ICON, menuVisible } from './Layout';
import NotificationBell from '../NotificationBell';
import ContadorPanel from '../../pages/ContadorPanel';

// Diseño alternativo tipo Office: barra superior con secciones (fila 1) y una
// "cinta" (ribbon) con los botones/iconos de la sección activa (fila 2).
// build: topbar-onerow-scroll
export default function LayoutTop({ children }) {
  const { user, logout, actingTenant, setActingTenant } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const handleLogout = () => { logout(); navigate('/login'); };
  const cambiarEmpresa = () => { setActingTenant(null); navigate('/dashboard'); };

  // Contador sin empresa seleccionada → panel de selección de empresas.
  if (user?.es_contador && !actingTenant) {
    return <ContadorPanel />;
  }

  const items = menuVisible(user);
  // Agrupar por secciones (los ítems sueltos van a una sección "Inicio").
  const sueltos = []; const grupos = []; let cur = null;
  for (const it of items) {
    if (it.sep) { cur = { sep: it.sep, items: [] }; grupos.push(cur); }
    else if (cur) cur.items.push(it); else sueltos.push(it);
  }
  // "Inicio" = ítems sueltos (Dashboard) + los de "Analítica" (Reportes).
  const analitica = grupos.find(g => g.sep === 'Analítica');
  const inicioItems = [...sueltos, ...(analitica ? analitica.items : [])];
  const secciones = [
    ...(inicioItems.length ? [{ sep: 'Inicio', items: inicioItems }] : []),
    ...grupos.filter(g => g.sep !== 'Analítica'),
  ];

  const baseOf = (p) => p.split('?')[0];
  const here = location.pathname + location.search;
  const esActivo = (it) => it.path.includes('?') ? here === it.path : location.pathname === baseOf(it.path);
  const activa = secciones.find(g => g.items.some(it => location.pathname.startsWith(baseOf(it.path))))?.sep;
  const [sel, setSel] = useState(activa || secciones[0]?.sep);
  const seccion = secciones.find(g => g.sep === sel) || secciones[0];

  if (user?.es_super_admin && !user?.tenant_id) {
    return <ShellSoloSuper user={user} onLogout={handleLogout} navigate={navigate}>{children}</ShellSoloSuper>;
  }

  return (
    <div style={s.shell}>
      <style>{`.erp-secciones::-webkit-scrollbar{height:0;width:0;background:transparent}.erp-secciones{-ms-overflow-style:none;scrollbar-width:none;scroll-behavior:smooth}`}</style>
      {/* Fila 1: secciones a todo el ancho + botón salir */}
      <div style={s.topbar}>
        <div style={s.brandTop}>Snel<span style={{ color: '#3b82f6' }}>ERP</span></div>
        <div className="erp-secciones" style={s.secciones}>
          <div style={s.seccionesInner}>
            {secciones.map(g => (
              <button key={g.sep} onClick={() => { setSel(g.sep); if (g.items[0]) navigate(g.items[0].path); }}
                style={{ ...s.secTab, ...(sel === g.sep ? s.secTabOn : {}) }}>
                <span style={s.secIco}>{SEP_ICON[g.sep] || g.items[0]?.icon}</span>{g.sep}
              </button>
            ))}
          </div>
        </div>
        <div style={s.campana}><NotificationBell /></div>
        <div style={s.userBox}>
          <span style={s.uName}>{user?.nombre}</span>
          <span style={s.uRole}>{(user?.perfil_nombre || user?.rol || '').toUpperCase()}</span>
        </div>
        <button style={s.salirBtn} onClick={handleLogout} title={`Salir (${user?.nombre || ''})`}>
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="16" height="16" strokeLinecap="round" strokeLinejoin="round"><path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"/><polyline points="16 17 21 12 16 7"/><line x1="21" y1="12" x2="9" y2="12"/></svg>
        </button>
      </div>

      {/* Fila 2: cinta con los botones de la sección activa */}
      <div style={s.ribbon}>
        {(seccion?.items || []).map(it => (
          <button key={it.path} onClick={() => navigate(it.path)}
            style={{ ...s.rbtn, ...(esActivo(it) ? s.rbtnOn : {}) }}>
            <span style={s.rico}>{it.icon}</span>
            <span style={s.rlbl}>{it.label}</span>
          </button>
        ))}
        <div id="ribbon-actions" style={s.ribbonActions} />
      </div>

      {user?.es_contador && actingTenant && (
        <div style={{ background: '#1e3a5f', borderBottom: '1px solid #3b82f6', color: '#bfdbfe', padding: '6px 14px', fontSize: 12.5, display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 12, flexShrink: 0 }}>
          <span>👤 Estás revisando <strong style={{ color: '#fff' }}>{actingTenant.nombre}</strong> como contador</span>
          <button onClick={cambiarEmpresa} style={{ background: '#0f172a', border: '1px solid #3b82f6', color: '#93c5fd', borderRadius: 6, padding: '3px 10px', fontSize: 12, cursor: 'pointer' }}>Cambiar empresa</button>
        </div>
      )}
      <SuscripcionBanner su={user?.suscripcion} />
      <div style={s.content}>{children}</div>
    </div>
  );
}

// Aviso de vencimiento de suscripción / prueba (solo tenants; se oculta si falta > 15 días).
function SuscripcionBanner({ su }) {
  if (!su || su.fecha_vencimiento == null) return null;
  const dias = Number(su.dias_restantes);
  if (!su.es_prueba && dias > 15) return null; // suscripción con holgura: no molestar
  const fecha = new Date(su.fecha_vencimiento).toLocaleDateString('es-CO');
  const critico = dias <= 3;
  const bg = critico ? '#450a0a' : '#422006';
  const bd = critico ? '#b91c1c' : '#a16207';
  const col = critico ? '#fca5a5' : '#fde68a';
  let texto;
  if (dias <= 0) texto = su.es_prueba ? `Tu prueba gratuita venció el ${fecha}.` : `Tu suscripción venció el ${fecha}.`;
  else if (su.es_prueba) texto = `🎁 Prueba gratuita: te queda${dias === 1 ? '' : 'n'} ${dias} día${dias === 1 ? '' : 's'} (vence el ${fecha}). Contrata el plan anual para no perder el acceso.`;
  else texto = `⏳ Tu suscripción vence en ${dias} día${dias === 1 ? '' : 's'} (${fecha}). Renueva para no interrumpir el servicio.`;
  return (
    <div style={{ background: bg, borderBottom: `1px solid ${bd}`, color: col, padding: '7px 14px', fontSize: 12.5, textAlign: 'center', flexShrink: 0 }}>
      {texto}
    </div>
  );
}

function ShellSoloSuper({ user, onLogout, navigate, children }) {
  return (
    <div style={s.shell}>
      <div style={s.topbar}>
        <div style={s.brand}><span style={s.logo}>ERP</span><span style={s.brandName}>Plataforma</span></div>
        <div style={s.secciones}><button style={{ ...s.secTab, ...s.secTabOn }} onClick={() => navigate('/super-admin')}>Super Admin</button></div>
        <div style={s.userArea}><span style={s.userName}>{user?.nombre}</span><button style={s.salir} onClick={onLogout}>Salir</button></div>
      </div>
      <div style={s.content}>{children}</div>
    </div>
  );
}

const s = {
  shell: { height: '100vh', display: 'flex', flexDirection: 'column', background: '#0f172a', color: '#f1f5f9', fontFamily: 'system-ui,sans-serif', overflow: 'hidden' },
  topbar: { display: 'flex', alignItems: 'flex-end', gap: 8, background: '#1e293b', padding: '24px 10px 0', minHeight: 58, flexShrink: 0 },
  brandTop: { fontWeight: 800, fontSize: 20, color: '#f1f5f9', whiteSpace: 'nowrap', letterSpacing: .3, paddingBottom: 8, flexShrink: 0 },
  campana: { display: 'flex', alignItems: 'center', paddingBottom: 4, flexShrink: 0 },
  userBox: { display: 'flex', flexDirection: 'column', alignItems: 'flex-end', lineHeight: 1.15, paddingBottom: 6, flexShrink: 0 },
  uName: { color: '#e2e8f0', fontSize: 11, fontWeight: 600, whiteSpace: 'nowrap' },
  uRole: { color: '#64748b', fontSize: 8.5, fontWeight: 600, letterSpacing: .4, whiteSpace: 'nowrap' },
  brand: { display: 'flex', alignItems: 'center', gap: 8, flexShrink: 0 },
  logo: { background: '#3b82f6', color: '#fff', borderRadius: 6, padding: '3px 7px', fontSize: 12, fontWeight: 700, letterSpacing: 1 },
  brandName: { fontWeight: 600, fontSize: 14, whiteSpace: 'nowrap' },
  secciones: { display: 'flex', flex: 1, minWidth: 0, overflowX: 'auto', alignItems: 'flex-end', scrollbarWidth: 'none', msOverflowStyle: 'none' },
  seccionesInner: { display: 'flex', gap: 2, margin: '0 auto', alignItems: 'flex-end', flexShrink: 0 },
  secTab: { display: 'flex', alignItems: 'center', gap: 4, background: 'none', border: 'none', color: '#94a3b8', padding: '8px 6px', borderRadius: '8px 8px 0 0', fontSize: 11.5, fontWeight: 500, cursor: 'pointer', whiteSpace: 'nowrap', outline: 'none' },
  secTabOn: { background: '#172033', color: '#f1f5f9', fontWeight: 600 },
  secIco: { display: 'flex' },
  userArea: { display: 'flex', alignItems: 'center', gap: 10, flexShrink: 0 },
  badge: { background: '#1e40af', color: '#93c5fd', fontSize: 11, padding: '3px 8px', borderRadius: 20, fontWeight: 600 },
  userName: { color: '#cbd5e1', fontSize: 13, whiteSpace: 'nowrap' },
  salir: { background: '#450a0a', border: '1px solid #7f1d1d', color: '#fca5a5', padding: '5px 12px', borderRadius: 6, cursor: 'pointer', fontSize: 12 },
  salirBtn: { display: 'flex', alignItems: 'center', justifyContent: 'center', width: 34, height: 34, flexShrink: 0, background: '#450a0a', border: '1px solid #7f1d1d', color: '#fca5a5', borderRadius: 8, cursor: 'pointer' },
  ribbon: { display: 'flex', gap: 4, alignItems: 'stretch', flexWrap: 'wrap', background: '#172033', borderBottom: '1px solid #334155', padding: '8px 12px', flexShrink: 0 },
  rbtn: { display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 5, minWidth: 76, padding: '6px 8px', borderRadius: 8, textDecoration: 'none', color: '#cbd5e1', background: 'transparent', border: '1px solid transparent', cursor: 'pointer', fontFamily: 'inherit', transition: 'background .15s', flexShrink: 0, outline: 'none' },
  rbtnOn: { background: 'rgba(59,130,246,.16)', color: '#93c5fd', borderColor: '#3b82f6' },
  ribbonActions: { marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 8, alignSelf: 'center' },
  rico: { display: 'flex', transform: 'scale(1.25)' },
  rlbl: { fontSize: 11, textAlign: 'center', lineHeight: 1.2, maxWidth: 88 },
  content: { flex: 1, overflowY: 'auto', padding: 24 },
};
